import React, { useState } from 'react';
import {
  useThemeMode,
  systemMode,
} from '../../../_metronic/partials/layout/theme-mode/ThemeModeProvider';

type MetricItem = {
  label: string;
  value: string | number;
  color?: string;
};

type JobCardsAndTreatmentsTabbedWidgetProps = {
  openJobs: number;
  inProgressJobs: number;
  closedTodayJobs: number;
  closedLast7DaysJobs: number;
  treatmentStats: {
    todo: number;
    inProgress: number;
    completed: number;
  };
  jobCardsChartSeries?: any[];
  treatmentsChartSeries?: any[];
  onClick?: () => void;
};

export const JobCardsAndTreatmentsTabbedWidget: React.FC<
  JobCardsAndTreatmentsTabbedWidgetProps
> = ({
  openJobs,
  inProgressJobs,
  closedTodayJobs,
  closedLast7DaysJobs,
  treatmentStats,
  onClick,
}) => {
  const { mode } = useThemeMode();
  const isDarkMode =
    mode === 'dark' || (mode === 'system' && systemMode === 'dark');
  const [activeTab, setActiveTab] = useState<'jobcards' | 'treatments'>(
    'jobcards'
  );

  const iconColor = '#009ef7';

  const jobCardsMetrics: MetricItem[] = [
    { label: 'Open', value: openJobs },
    { label: 'In Progress', value: inProgressJobs },
    { label: 'Closed Today', value: closedTodayJobs },
    { label: 'Closed (7d)', value: closedLast7DaysJobs },
  ];

  const treatmentsMetrics: MetricItem[] = [
    { label: 'TODO', value: treatmentStats.todo },
    { label: 'In Progress', value: treatmentStats.inProgress },
    { label: 'Completed', value: treatmentStats.completed },
    {
      label: 'Total',
      value:
        treatmentStats.todo +
        treatmentStats.inProgress +
        treatmentStats.completed,
    },
  ];

  const currentMetrics =
    activeTab === 'jobcards' ? jobCardsMetrics : treatmentsMetrics;

  return (
    <div
      className={`card h-100 ${onClick ? 'cursor-pointer hover-elevate-up' : ''}`}
      onClick={onClick}
      style={{
        backgroundColor: isDarkMode ? '#1e1e2d' : '#ffffff',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Decorative background gradient */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '200px',
          height: '200px',
          background: `radial-gradient(circle at top right, ${iconColor}15, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />

      <div className="card-body p-4 position-relative">
        {/* Header */}
        <div className="d-flex align-items-center mb-4">
          <div
            className="symbol symbol-40px me-2"
            style={{
              backgroundColor: `${iconColor}20`,
              border: `2px solid ${iconColor}40`,
            }}
          >
            <div className="symbol-label">
              <i
                className="bi bi-card-checklist fs-2"
                style={{ color: iconColor }}
              ></i>
            </div>
          </div>
          <div>
            <span
              className={`${isDarkMode ? 'text-white' : 'text-gray-800'} fw-bolder fs-5 d-block`}
            >
              Job Cards & Treatments
            </span>
            <span
              className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'} fw-semibold fs-8`}
            >
              Live metrics
            </span>
          </div>
        </div>

        {/* Tabs */}
        <ul className="nav nav-tabs nav-line-tabs mb-3 fs-7">
          <li className="nav-item">
            <a
              className={`nav-link ${activeTab === 'jobcards' ? 'active' : ''}`}
              style={{
                cursor: 'pointer',
                color:
                  activeTab === 'jobcards'
                    ? iconColor
                    : isDarkMode
                      ? '#a1a5b7'
                      : '#7e8299',
                borderBottomColor:
                  activeTab === 'jobcards' ? iconColor : 'transparent',
                fontWeight: activeTab === 'jobcards' ? '600' : '500',
                padding: '0.5rem 1rem',
              }}
              onClick={(e) => {
                e.stopPropagation();
                setActiveTab('jobcards');
              }}
            >
              Job Cards
            </a>
          </li>
          <li className="nav-item">
            <a
              className={`nav-link ${activeTab === 'treatments' ? 'active' : ''}`}
              style={{
                cursor: 'pointer',
                color:
                  activeTab === 'treatments'
                    ? iconColor
                    : isDarkMode
                      ? '#a1a5b7'
                      : '#7e8299',
                borderBottomColor:
                  activeTab === 'treatments' ? iconColor : 'transparent',
                fontWeight: activeTab === 'treatments' ? '600' : '500',
                padding: '0.5rem 1rem',
              }}
              onClick={(e) => {
                e.stopPropagation();
                setActiveTab('treatments');
              }}
            >
              Treatments
            </a>
          </li>
        </ul>

        {/* Content Area: Metrics */}
        <div className="row g-0">
          {/* Metrics Column */}
          <div className="col-12">
            <div className="row g-3">
              {currentMetrics.map((metric, index) => (
                <div key={index} className="col-6">
                  <div className="position-relative" style={{ paddingLeft: '10px' }}>
                    {/* Accent bar on the left */}
                    <div
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: '3px',
                        bottom: '3px',
                        width: '3px',
                        borderRadius: '3px',
                        background: `linear-gradient(180deg, ${iconColor}, ${iconColor}80)`,
                        boxShadow: `0 0 8px ${iconColor}40`,
                      }}
                    />
                    {/* Metric content */}
                    <div className="d-flex flex-column">
                      <div className="d-flex align-items-center justify-content-between mb-1">
                        <span
                          className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} fw-semibold fs-9 text-uppercase`}
                        >
                          {metric.label}
                        </span>
                      </div>
                      <div className="d-flex align-items-baseline">
                        <span
                          className={`fw-bolder ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                          style={{
                            fontSize: '1.5rem',
                            lineHeight: '1.2',
                            letterSpacing: '-0.02em',
                            ...(metric.color ? { color: metric.color } : {}),
                          }}
                        >
                          {metric.value}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};
